"use client";
import TimelineClient from "./TimelineClient";
import { HowItWorksData } from "@/types/data";
import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";

export default function HowItWorks({
  data,
}: {
  data: HowItWorksData["content"];
}) {
  const { tagline, title, description, steps } = data;

  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!headerRef.current) return;

    const ctx = gsap.context(() => {
      const elements = headerRef.current?.querySelectorAll("[data-animate]");

      if (elements) {
        gsap.fromTo(
          elements,
          {
            opacity: 0,
            y: 30,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: "power2.out",
            scrollTrigger: {
              trigger: headerRef.current,
              start: "top 80%",
              end: "bottom 20%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    }, headerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="bg-primary-light section-padding">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div className="flex flex-col justify-center">
            <div ref={headerRef}>
              {tagline && (
                <div data-animate className="flex items-center  mb-4">
                  <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
                  <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
                    {tagline}
                  </p>
                </div>
              )}

              {title && (
                <h2
                  data-animate
                  className="text-4xl md:text-5xl font-bold text-theme-secondary capitalize"
                >
                  {title}
                </h2>
              )}

              {description && (
                <p
                  data-animate
                  className="mt-4 text-lg text-theme-secondary-light md:max-w-[546px] "
                >
                  {description}
                </p>
              )}
            </div>
          </div>

          {steps && steps.length > 0 && <TimelineClient steps={steps} />}
        </div>
      </div>
    </section>
  );
}
